/**
 * Runtime validation for product events (spec 68 §6.6.5).
 *
 * Compile-time the `track<E>` signature guarantees a known event + prop
 * shape. At runtime we still validate (data can arrive from a request
 * body); a miss drops the event + `log.warn('amplitude.schema_miss')`.
 */
import type { z } from 'zod';
import { eventSchemas } from './events/index.js';

interface EventDef {
  schema: z.ZodTypeAny;
  schemaVer: string;
}

const registry = eventSchemas as unknown as Record<string, EventDef>;

export interface ValidationIssue {
  path: string;
  message: string;
}

export type ValidationResult = { ok: true; data: Record<string, unknown> } | { ok: false; issues: ValidationIssue[] };

export function isKnownEvent(event: string): boolean {
  return Object.hasOwn(registry, event);
}

export function getSchemaVer(event: string): string {
  return registry[event]?.schemaVer ?? '0-0-0';
}

export function validateProps(event: string, props: unknown): ValidationResult {
  const def = registry[event];
  if (!def) {
    return { ok: false, issues: [{ path: '', message: `unknown_event:${event}` }] };
  }
  const parsed = def.schema.safeParse(props);
  if (parsed.success) {
    return { ok: true, data: parsed.data as Record<string, unknown> };
  }
  const issues = parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message }));
  return { ok: false, issues };
}
